/**************************************************************************************************
 *
 *  This is the main container for all project report views: 
 *
 *  This views are individual components that will fit into the detail-container parent element
 *  defined in this container.
 *
**************************************************************************************************/

import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Home from '../../containers/Home';
import ErrorPage from '../../containers/ErrorPage';
import BasePage from '../../baseClasses/BasePage';
import Header from '../../components/Header';
import animator from '../../helpers/animator';
import dataModel from '../../models/DataModel';

import FileUtil from '../../helpers/FileUtil/FileUtil';
import ReferenceDocumentSearchFilter from './ReferenceDocumentSearchFilter';
import ReferenceDocumentsTable from './ReferenceDocumentsTable';
import ReferenceDocumentsUploader from './ReferenceDocumentsUploader';
import ReferenceDocumentDisplay from './ReferenceDocumentDisplay';
import Metadata from '../../helpers/metadata/Metadata';
import fileUtil from '../../helpers/FileUtil/FileUtil';
import AnalyticsLogger from '../../helpers/analytics/analyticsLogger';

import './ReferenceDocumentsLibrary.scss';

// Change ReferenceDocumentsLibrary to the appropriate name
class ReferenceDocumentsLibrary extends BasePage {

  constructor(props) {
    super(props);

    // Initialize all expected state variable here

    this.state = {
      view: null,
      sortField: 'created_at',
      files: [],
    };
    this.handleDeleteDocument = this.handleDeleteDocument.bind(this);

    this.animator = new animator();
    this.dontDisplay = true;

    const currentRole = props.currentUser ? props.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.referenceDocuments != this.props.referenceDocuments && this.props.referenceDocuments) {
      this.hasData = true;
      this.setState({ referenceDocuments: this.props.referenceDocuments });
    }
    if (prevProps.currentCompany != this.props.currentCompany && this.props.permissions && this.props.currentCompany) {
      this.handleSortChange('created_at', false, true);
    }
    if ((!prevProps.match && this.props.match) || prevProps.match.params != this.props.match.params) {
      this.navigateToReferenceDocument(this.props.match.params, this.props.permissions);
    }
  }

  componentDidMount() {
    this.dontDisplay = false;
    this.hasData = false;
    super.componentDidMount();
    if (!this.props.referenceDocuments) {
      this.handleSortChange('created_at', false, true);
    }
    else {
      this.handleSortChange('created_at', false, true);
      this.setState({ referenceDocuments: this.props.referenceDocuments });
    }
    if (this.props.match.params) {
      this.navigateToReferenceDocument(this.props.match.params);
    }
  }

  navigateToReferenceDocument(params) {
    if (!params || !params.group_identifier) {
      this.setState({ referenceDocument: null });
    }
    else if (params.group_identifier) {
      dataModel.referenceDocumentsDAO.getGroupIdentifier(params.group_identifier).then(referenceDocument => {
        this.setState({ referenceDocument: referenceDocument[0] });
      });
    }
  }

  componentWillUnmount() {
    this.hasData = false;
  }

  handleSelectReferenceDocument(referenceDocument) {
    if (referenceDocument) {
      this.captureEventHistory(referenceDocument);
      this.props.history.push('/reference-documents/document/' + referenceDocument.group_identifier);
    }
    else {
      this.props.history.push('/reference-documents');
    }
  }

  captureEventHistory(referenceDocument) {
    const extension = referenceDocument.name.split('.')[1];
    const body = `${this.props.currentUser.name} downloaded ${referenceDocument.name}`;
    Metadata.logEventHistory('Reference Document Download', extension, body);
    this.analyticsLogger.logRDLDownloadEvent(null, 'DocumentLibraryRDViewed');
  }

  getSystemPopupView() {
    if (this.props.systemPopup) {
      return this.props.systemPopup;
    }
    else {
      return null;
    }
  }

  handleFilterChange(filter, dontSearch) {

    let dateRange = this.handleCreateDateRange(filter);

    let height = Math.floor((window.innerHeight - 160) / 32);
    this.props.referenceDocumentsDataModel.referenceDocuments = { clear: true };
    let self = this;
    this.props.referenceDocumentsDataModel.fetchReferenceDocuments(this.state.searchString,
      filter && filter.documentType ? filter.documentType : '',
      filter && filter.market ? filter.market : '',
      dateRange,
      this.state.sortField,
      this.state.ascending,
      (referenceDocuments) => {
        self.setState({ filter, referenceDocuments });
      },
      height);
  }

  handleSearchChange(searchString) {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    let height = Math.floor((window.innerHeight - 160) / 32);
    const self = this;
    const filter = this.state.filter;
    let dateRange = this.handleCreateDateRange(filter);
    this.refresh = true;
    this.searchTimer = setTimeout(() => {
      if (self.props.permissions && self.props.permissions.canCreateReferenceDocument) {
        self.props.referenceDocumentsDataModel.referenceDocuments = { clear: true };
        self.props.referenceDocumentsDataModel.fetchReferenceDocuments(searchString,
          filter && filter.documentType ? filter.documentType : '',
          filter && filter.markets ? filter.markets : '',
          dateRange,
          self.state.sortField,
          self.state.ascending,
          (searchStr) => {
            self.setState({ searchString: searchString });
          },
          height,
          1);
      }
    }, 400);
    this.setState({ searchString });
  }

  handleCreateDateRange(filter) {
    if (filter) {
      return {
        start_date: filter.startDate,
        end_date: filter.endDate,
      };
    }
    return {};
  }

  handleSortChange(sortField, ascending, noRefresh) {
    const { filter } = this.state;
    let dateRange = this.handleCreateDateRange(filter);
    let height = Math.floor((window.innerHeight - 160) / 32);
    this.refresh = !noRefresh;
    const self = this;
    this.props.referenceDocumentsDataModel.referenceDocuments = { clear: true };
    this.props.referenceDocumentsDataModel.fetchReferenceDocuments(this.state.searchString,
      filter && filter.document_type ? filter.document_type : '',
      filter && filter.markets ? filter.markets : '',
      dateRange,
      sortField,
      ascending,
      (referenceDocuments) => {
        self.hasData = true;
        self.setState({
          sortField: sortField,
          ascending: ascending,
          referenceDocuments: referenceDocuments,
        });
      }, height);
  }

  handleGetMoreReferenceDocuments(callBack) {
    const { filter } = this.state;
    let dateRange = this.handleCreateDateRange(filter);
    let perPage = Math.floor((window.innerHeight - 160) / 32);
    this.refresh = false;
    this.props.referenceDocumentsDataModel.fetchReferenceDocuments(this.state.searchString,
      filter && filter.document_type ? filter.document_type : '',
      filter && filter.markets ? filter.markets : '',
      dateRange,
      this.state.sortField,
      this.state.ascending,
      callBack,
      perPage);
  }

  handleAdd() {
    this.setState({ displayAddModal: true });
  }
  handleClose() {
    this.setState({ displayAddModal: false, files: [] });
  }

  handleDeleteDocument(docId){
    const { referenceDocuments } = this.state;
    const updatedList = referenceDocuments.filter(item => item.id != docId);
    this.setState({ referenceDocuments: updatedList });
    window.location.reload();
  }

  handleFileSelect(file, imagePreview) {
    let refDoc = {};
    refDoc.upload = file;
    refDoc.name = file.name;

    const fileType = fileUtil.getExtension(refDoc.name);
    this.analyticsLogger.logRDLUploadEvent(fileType, true);

    if (this.props.referenceDocumentsDataModel) {
      let index = this.state.files.length;
      this.props.referenceDocumentsDataModel.referenceDocument = {
        create: refDoc,
        callBack: (file) => {
          let files = this.state.files;
          if (file.error) {
            files[index].successful = false;
          }
          else {
            files[index].successful = true;
          }
          files[index].finished = true;
          this.setState({ files: files });
        },
      };
      let files = this.state.files;
      files.push({
        file: file,
        finished: false,
        ext: FileUtil.getExtension(file.name),
        preview: imagePreview,
      });
      this.setState({ files: files });
    }
  }

  render() {
    if (this.dontDisplay) {
      return <div />;
    }
    if (this.props.catastrophicError) {
      return <ErrorPage catastrophicError={this.props.catastrophicError} />;
    }
    let waitSpinner = null;
    if (this.props.inProgress) {
      waitSpinner = <Home type="transparent" />;
    }
    if (!this.props.currentUser || !this.props.currentCompany || !this.props.permissions) {
      return <Home type={'referenceDocuments'} message={'Awaiting Initialization'} />;
    }
    this.setWhiteLabels(this.props.currentCompany);

    if (this.props.permissions) {
      if (this.props.permissions && this.props.permissions.canCreateReferenceDocument) {
        if ((!this.props.referenceDocuments && !this.hasData)) {
          return <Home type={'referenceDocuments'} message={'Fetching All Reference Documents'} />;
        }
      }
      else {
        this.props.history.replace('/');
        return null;
      }
    }

    const { displayAddModal } = this.state;

    const systemPopup = this.getSystemPopupView();

    let referenceDocumentSearchFilter = <ReferenceDocumentSearchFilter searchString={this.state.searchString} handleFilterChange={this.handleFilterChange} handleSearchChange={this.handleSearchChange} selectedFilters={this.state.filter} />;

    const actionButton = <div className={'ref-docs-add-button' + (displayAddModal ? ' ref-docs-add-button--rotated' : '')} onClick={displayAddModal ? this.handleClose : this.handleAdd} />;
    let refDocUploader;
    if (displayAddModal) {
      refDocUploader = <ReferenceDocumentsUploader
        handleFileSelect={this.handleFileSelect}
        files={this.state.files} />;
    }
    return <div className="reference-documents-container" ref={element => {
      this.mainContainer = element;
    }}>
      <Header selectedPage="reference-documents" />
      {systemPopup}
      {waitSpinner}
      {this.state.referenceDocument ?
        (<ReferenceDocumentDisplay referenceDocument={this.state.referenceDocument} />) :
        (<div className="reference-documents-body">
          <div className="reference-documents-details">
            <div className="reference-documents-title">
              Reference Document Library
            </div>
            {actionButton}
            {refDocUploader}
            <div className="reference-documents-search">
              {referenceDocumentSearchFilter}
            </div>
          </div>
          <ReferenceDocumentsTable
            refresh={this.refresh}
            selectReferenceDocument={this.handleSelectReferenceDocument}
            filter={this.state.filter}
            searchString={this.state.searchString}
            referenceDocuments={this.state.referenceDocuments}
            sortField={this.state.sortField}
            ascending={this.state.ascending}
            handleSortChange={this.handleSortChange}
            getMoreReferenceDocuments={this.handleGetMoreReferenceDocuments}
            handleDeleteDocument={this.handleDeleteDocument}
            hasBannerDisplay={this.props.globalBanner && this.props.globalBanner.bannerType ? true : false}
          />
        </div>)}
    </div>;
  }
}

// Change to appropriate class name

const mapStateToProps = (state, ownProps) => {

  return {
    catastrophicError: state.main.catastrophicError,
    inProgress: state.main.inProgress,
    dataModel: dataModel,
    referenceDocumentsDataModel: dataModel.referenceDocumentsDataModel,
    referenceDocuments: state.referenceDocuments.referenceDocuments,
    permissions: state.new_users ? state.new_users.permissions : null,
    systemPopup: state.main.systemPopup,
    currentUser: state.new_users.currentUser,
    currentCompany: state.new_companies.currentCompany,
    globalBanner: state.main.globalBanner,
  };
};

export default withRouter(connect(mapStateToProps)(ReferenceDocumentsLibrary));



// WEBPACK FOOTER //
// ./src/containers/ReferenceDocumentsLibrary/index.js